import { Box, Button, Group, Paper, Stack, Text } from "@mantine/core";
import CountryFlag from "@/components/shared/CountryFlag";
import type { ComingSoonEcosystemCorridor } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import {
  comingSoonCorridorHasFlags,
  formatComingSoonCorridorPair,
} from "@/shared/constants/comingSoonEcosystemCorridors.const";

const CARD_BORDER = "#BFDBFE";
const CARD_BLUE = "#2563EB";
const CARD_NAVY = "#04305c";
const FLAG_BORDER = "#BFDBFE";

type EcosystemCorridorPlaceholderCardProps = {
  corridor: ComingSoonEcosystemCorridor;
  /** Fixed width for horizontal scroll layouts (e.g. explore page). */
  fixedWidth?: number;
  /** Use compact flag badges (sector carousel) vs inline flags (explore grid). */
  variant?: "carousel" | "explore";
};

function FlagBadge({ country }: { country: string }) {
  return (
    <Box
      style={{
        padding: 8,
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        border: `1px solid ${FLAG_BORDER}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box style={{ transform: "scale(1.4)", transformOrigin: "center", lineHeight: 0 }}>
        <CountryFlag country={country} />
      </Box>
    </Box>
  );
}

export default function EcosystemCorridorPlaceholderCard({
  corridor,
  fixedWidth,
  variant = "carousel",
}: EcosystemCorridorPlaceholderCardProps) {
  const isUnderway = corridor.status === "underway";
  const buttonLabel = isUnderway ? "Underway" : "Coming Soon";
  const showFlags = comingSoonCorridorHasFlags(corridor);
  const corridorPair = formatComingSoonCorridorPair(corridor);

  const flagsRow = showFlags ? (
    variant === "carousel" ? (
      <Group gap="sm" justify="center" wrap="nowrap">
        <FlagBadge country={corridor.country!} />
        <Text fw={700} size="lg" c={CARD_BLUE} lh={1}>
          ↔
        </Text>
        <FlagBadge country={corridor.targetCountry!} />
      </Group>
    ) : (
      <Group gap="xs" mb="sm">
        <CountryFlag country={corridor.country} />
        <CountryFlag country={corridor.targetCountry} />
      </Group>
    )
  ) : null;

  return (
    <Paper
      radius="lg"
      shadow="none"
      h="100%"
      mih={188}
      style={{
        border: `1px solid ${CARD_BORDER}`,
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        ...(fixedWidth ? { flex: "0 0 auto", width: fixedWidth, maxWidth: "85vw" } : undefined),
      }}
    >
      <Stack gap="md" h="100%" p="lg" justify="space-between">
        <Stack gap="sm" align={variant === "carousel" ? "center" : "stretch"} style={{ flex: 1 }}>
          {flagsRow}

          <Stack gap={4} align={variant === "carousel" ? "center" : "stretch"}>
            <Text
              fw={700}
              size="sm"
              tt="uppercase"
              ta={variant === "carousel" ? "center" : "left"}
              c="#04305c"
              lineClamp={2}
              lh={1.35}
            >
              {corridorPair}
            </Text>
            <Text
              fw={600}
              size="xs"
              tt="uppercase"
              ta={variant === "carousel" ? "center" : "left"}
              c="#04305c"
              lineClamp={2}
              lh={1.35}
              style={{ opacity: 0.88 }}
            >
              {corridor.corridorSubtitle}
            </Text>
          </Stack>
        </Stack>

        <Button
          size="sm"
          fullWidth
          disabled
          styles={{
            root: {
              minHeight: 38,
              fontWeight: 600,
              backgroundColor: CARD_NAVY,
              color: "#FFFFFF",
              opacity: 1,
              "&[data-disabled]": {
                backgroundColor: CARD_NAVY,
                color: "#FFFFFF",
                opacity: 1,
              },
            },
          }}
        >
          {buttonLabel}
        </Button>
      </Stack>
    </Paper>
  );
}
